<template>
  <div
    class="btn-wrapper"
    v-click-outside="closeDropdown"
    :class="{ center: center }"
    v-tooltip.top="showTooltip && tooltipDisplay"
  >
    <component
      :is="componentType"
      v-bind="buttonAttrs"
      class="tw:relative tw:inline-flex tw:items-center tw:gap-x-1.5 tw:rounded-md tw:px-3 tw:py-2  tw:shadow-xs tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:hover:no-underline tw:not-disabled:cursor-pointer tw:transition-opacity"
      :class="{
        'tw:text-sm tw:font-medium': !small,
        'tw:bg-button-primary tw:text-button-primary-text tw:not-disabled:hover:bg-button-primary-hover tw:focus-visible:outline-button-primary': primary && !danger,
        'tw:bg-button-secondary tw:text-button-secondary-text tw:inset-ring tw:inset-ring-border tw:not-disabled:hover:bg-button-secondary-hover': !primary && !danger,
        'tw:text-xs tw:font-medium': small,
        'tw:bg-button-danger tw:text-button-danger-text tw:not-disabled:hover:bg-button-danger-hover tw:focus-visible:outline-button-danger': primary && danger,
        'tw:bg-button-secondary-danger tw:text-button-secondary-danger-text tw:inset-ring tw:inset-ring-border tw:not-disabled:hover:bg-button-secondary-danger-hover': !primary && danger,
        'tw:rounded-r-none tw:mr-0': hasDropdown,
        'tw:my-3': hasVerticalMargins,
        'tw:opacity-60 tw:cursor-not-allowed': isDisabled,
        'no-underline': isLink,
      }"
      :style="{ width: width }"
      @click="onClick"
    >
      <transition name="fade">
        <div class="btn__spinner">
          <span
            v-if="loading"
            class="ks-spinner"
            :class="{ 'ks-spinner--dark': !primary }"
          ></span>
        </div>
      </transition>

      <span class="btn__label" :class="{ 'btn__label--hidden': loading }">{{
        label
      }}</span>
    </component>

    <button
      v-if="hasDropdown"
      class="tw:relative tw:inline-flex tw:items-center tw:gap-x-1.5 tw:rounded-r-md tw:px-1 tw:-ml-px tw:shadow-xs tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:hover:no-underline tw:not-disabled:cursor-pointer"
      :class="{
        'tw:bg-button-primary tw:text-button-primary-text tw:hover:bg-button-primary-hover tw:focus-visible:outline-button-primary tw:border-l-ks-black/20 tw:border-l': primary,
        'tw:bg-button-secondary tw:text-button-secondary-text tw:inset-ring tw:inset-ring-border tw:hover:bg-button-secondary-hover': !primary && !danger,
        'tw:text-xs tw:font-medium': small,
        'tw:bg-button-danger tw:text-button-danger-text tw:hover:bg-button-danger-hover tw:focus-visible:outline-button-danger': danger,
        'btn--disabled': isDisabled,
        'tw:my-3': hasVerticalMargins,
      }"
      :disabled="isDisabled"
      @click.prevent="toggleDropdown"
    >
      <svgicon class="ks-icon-size-xs" icon="ks-icon-caret" />
    </button>

    <transition name="fade">
      <div
        v-if="dropdownVisible"
        class="btn-dropdown-list"
        :class="{
          'btn-dropdown-list--has-vertical-margins': hasVerticalMargins,
        }"
      >
        <ks-button-dropdown-item
          v-for="item in dropdownItems"
          :key="item.label"
          :label="item.label"
          :click-function="item.clickFunction"
          :activity-type="item.activityType"
          :danger="item.danger"
          :stop-propagation="stopPropagation"
          :href="item.href"
          @click="onDropdownItemClick"
        />
      </div>
    </transition>
  </div>
</template>

<script>
  import svgicon from 'vue-svgicon'
  import 'Components/icons'
  import { ClickOutside } from 'Directives/ClickOutside'
  import ButtonMixin from 'Mixins/ButtonMixin.vue'
  import KsButtonDropdownItem from 'Components/form/KsButtonDropdownItem.vue'

  export default {
    name: 'ks-button',
    components: {
      svgicon,
      KsButtonDropdownItem,
    },
    directives: {
      ClickOutside,
    },
    mixins: [ ButtonMixin ],
    data () {
      return {
        dropdownOpen: false,
        hasFocus: false,
        observer: null,
      }
    },
    computed: {
      hasDropdown () {
        return this.dropdownItems.length > 0
      },
      dropdownVisible () {
        return this.dropdownOpen && this.hasDropdown
      },
    },
    methods: {
      closeDropdown () {
        this.dropdownOpen = false
      },
      openDropdown () {
        this.dropdownOpen = true
      },
      toggleDropdown (event) {
        if (this.stopPropagation) {
          event.stopPropagation()
        }

        this.dropdownOpen = !this.dropdownOpen
      },
      onDropdownItemClick (activityType) {
        this.closeDropdown()
        this.$emit('dropdown-click', activityType)
      },
    },
    props: {
      dropdownItems: {
        type: Array,
        default: () => [
          /* { label: 'Cancel Event', href: url, clickFunction: fn, danger: true } */
        ],
      },
      small: {
        type: Boolean,
        default: false,
      },
      width: {
        type: String,
      },
      center: {
        type: Boolean,
        default: false,
      },
      hasVerticalMargins: {
        type: Boolean,
        default: true,
      },
    },
  }

</script>

<style lang="scss">
  .btn-wrapper {
    &.center {
      > .btn {
        margin-left: auto;
        margin-right: auto;
      }
    }

    // I had SUCH a hard time getting this to work correctly.
    // The only way I could make the clone button turn gray was to put the code in this file
    // and make the styles !important. ¯\_(ツ)_/¯
    // TODO: Possibly make this a built-in part of the component, so it makes more sense to
    // have the styles in here, but idk.
    &.grayed-out > button {
      color: #bbb !important;
      border-color: #bbb !important;
    }
  }

  .btn {
    &__spinner {
      align-items: center;
      display: flex;
      justify-content: center;
      position: absolute;

      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
    }

    &__label {
      transition: opacity 0.5s;

      &--hidden {
        opacity: 0;
      }
    }
  }
</style>
